import React, { useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { alpha, useTheme } from '@mui/material/styles'
import {
  ArrowBack as ArrowBackIcon,
  BugReport as BugReportIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Email as EmailIcon
} from '@mui/icons-material'
import { getServerBaseUrl } from '@/utils/serverUrl'

interface TestResult {
  id: string
  name: string
  url: string
  method: string
  status: 'pending' | 'success' | 'error' | 'warning'
  statusCode?: number
  responseTime?: number
  response?: any
  error?: string
  timestamp: number
}

interface EndpointTest {
  id: string
  name: string
  description: string
  url: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  headers?: Record<string, string>
  body?: any
  expectedStatus?: number
  category: 'discovery' | 'health' | 'rooms' | 'company' | 'auth' | 'billing'
}

interface EndpointTestingPanelProps {
  onBack: () => void
}

function categoryChipPalette(theme: Theme, category: EndpointTest['category']) {
  const p = theme.palette
  switch (category) {
    case 'discovery':
      return { bgcolor: p.info.main, color: p.info.contrastText }
    case 'health':
      return { bgcolor: p.success.main, color: p.success.contrastText }
    case 'rooms':
      return { bgcolor: p.secondary.main, color: p.secondary.contrastText }
    case 'company':
      return { bgcolor: p.warning.main, color: p.warning.contrastText }
    case 'auth':
      return { bgcolor: p.error.main, color: p.error.contrastText }
    case 'billing':
      return { bgcolor: p.info.main, color: p.info.contrastText }
    default:
      return { bgcolor: p.primary.main, color: p.primary.contrastText }
  }
}

function statusAccent(theme: Theme, status: TestResult['status']) {
  switch (status) {
    case 'success':
      return theme.palette.success.main
    case 'error':
      return theme.palette.error.main
    case 'warning':
      return theme.palette.warning.main
    default:
      return theme.palette.text.secondary
  }
}

const ENDPOINT_TESTS: EndpointTest[] = [
  // Health & System endpoints
  {
    id: 'health-main',
    name: 'Main Health Check',
    description: 'Test the main health endpoint with system metrics',
    url: '/health',
    method: 'GET',
    category: 'health',
    expectedStatus: 200
  },
  {
    id: 'health-livekit',
    name: 'LiveKit Health',
    description: 'Test LiveKit connection health',
    url: '/health/livekit',
    method: 'GET',
    category: 'health',
    expectedStatus: 200
  },
  {
    id: 'performance-metrics',
    name: 'Performance Metrics',
    description: 'Test server performance metrics',
    url: '/api/performance-metrics',
    method: 'GET',
    category: 'health',
    expectedStatus: 200
  },
  
  // Lobby & Room endpoints
  {
    id: 'lobby-stats',
    name: 'Lobby Statistics',
    description: 'Test lobby statistics endpoint',
    url: '/api/lobby/stats',
    method: 'GET',
    category: 'rooms',
    expectedStatus: 200
  },
  {
    id: 'lobby-user-count',
    name: 'User Count',
    description: 'Test user count endpoint',
    url: '/api/lobby/user-count',
    method: 'GET',
    category: 'rooms',
    expectedStatus: 200
  },
  {
    id: 'lobby-company-usage',
    name: 'Company Usage',
    description: 'Test company usage statistics',
    url: '/api/lobby/company-usage',
    method: 'GET',
    category: 'company',
    expectedStatus: 200
  },
  {
    id: 'lobby-rooms-discover',
    name: 'Room Discovery',
    description: 'Test the company-filtered room discovery endpoint',
    url: '/api/lobby/rooms/discover',
    method: 'GET',
    category: 'discovery',
    expectedStatus: 200
  },
  {
    id: 'lobby-company-hydration',
    name: 'Company Hydration',
    description: 'Test company context readiness endpoint',
    url: '/api/lobby/company-hydration',
    method: 'GET',
    category: 'discovery',
    expectedStatus: 200
  },
  {
    id: 'lobby-performance-metrics',
    name: 'Lobby Performance',
    description: 'Test lobby-specific performance metrics',
    url: '/api/lobby/performance-metrics',
    method: 'GET',
    category: 'health',
    expectedStatus: 200
  },
  {
    id: 'lobby-test',
    name: 'Lobby Test',
    description: 'Test lobby endpoint functionality',
    url: '/api/lobby/test',
    method: 'GET',
    category: 'rooms',
    expectedStatus: 200
  },
  {
    id: 'lobby-refresh',
    name: 'Lobby Refresh',
    description: 'Test lobby refresh functionality',
    url: '/api/lobby/refresh',
    method: 'POST',
    category: 'rooms',
    expectedStatus: 200
  },
  
  // Default Rooms endpoints
  {
    id: 'default-rooms',
    name: 'Default Rooms',
    description: 'Test default rooms endpoint',
    url: '/api/default-rooms',
    method: 'GET',
    category: 'rooms',
    expectedStatus: 200
  },
  {
    id: 'default-rooms-recreate',
    name: 'Recreate Default Rooms',
    description: 'Test default rooms recreation',
    url: '/api/default-rooms/recreate',
    method: 'POST',
    category: 'rooms',
    expectedStatus: 200
  },
  
  // Company endpoints
  {
    id: 'company-lobby-rooms',
    name: 'Company Lobby Rooms',
    description: 'Test company lobby rooms endpoint',
    url: '/api/company-lobby-rooms',
    method: 'GET',
    category: 'company',
    expectedStatus: 200
  },
  {
    id: 'company-removal-preview',
    name: 'Company Removal Preview',
    description: 'Test company removal preview (requires companyId)',
    url: '/api/company/test-company/removal-preview',
    method: 'GET',
    category: 'company',
    expectedStatus: 200
  },
  
  // Room Control endpoints
  {
    id: 'room-control',
    name: 'Room Control',
    description: 'Test room control functionality',
    url: '/api/room-control',
    method: 'POST',
    category: 'rooms',
    expectedStatus: 200
  },
  {
    id: 'validate-room-password',
    name: 'Validate Room Password',
    description: 'Test room password validation',
    url: '/api/validate-room-password',
    method: 'POST',
    category: 'rooms',
    expectedStatus: 200
  },
  
  // Admin endpoints (Global Admin required)
  {
    id: 'admin-status',
    name: 'Admin Status',
    description: 'Test admin system status (Global Admin required)',
    url: '/api/admin/status',
    method: 'GET',
    category: 'auth',
    expectedStatus: 200
  },
  {
    id: 'admin-users',
    name: 'Admin Users',
    description:
      'Test admin users endpoint (Global Admin). Supports search, role, isPlatformAdmin, createdFrom/To, companyId, noCompany query params.',
    url: '/api/admin/users',
    method: 'GET',
    category: 'auth',
    expectedStatus: 200
  },
  {
    id: 'admin-companies',
    name: 'Admin Companies',
    description: 'Test admin companies endpoint (Global Admin required)',
    url: '/api/admin/companies',
    method: 'GET',
    category: 'auth',
    expectedStatus: 200
  },
  {
    id: 'admin-billing-tiers',
    name: 'Admin Billing Tiers',
    description: 'Test admin billing tiers endpoint (Global Admin required)',
    url: '/api/admin/billing-tiers',
    method: 'GET',
    category: 'auth',
    expectedStatus: 200
  },
  {
    id: 'admin-system-health',
    name: 'Admin System Health',
    description: 'Test admin system health endpoint (Admin access required)',
    url: '/api/admin/system-health',
    method: 'GET',
    category: 'auth',
    expectedStatus: 200
  },
  
  // Billing endpoints
  {
    id: 'billing-test',
    name: 'Billing Test',
    description: 'Test billing endpoint functionality',
    url: '/api/billing/test',
    method: 'GET',
    category: 'billing',
    expectedStatus: 200
  },
  {
    id: 'billing-auto-bill',
    name: 'Auto Bill New User',
    description: 'Test auto-billing for new users',
    url: '/api/billing/auto-bill-new-user',
    method: 'POST',
    category: 'billing',
    expectedStatus: 200
  },
  
  // Stripe endpoints
  {
    id: 'stripe-test',
    name: 'Stripe Test',
    description: 'Test Stripe integration endpoint',
    url: '/api/stripe/test',
    method: 'GET',
    category: 'billing',
    expectedStatus: 200
  },
  {
    id: 'stripe-config',
    name: 'Stripe Config',
    description: 'Test Stripe configuration endpoint',
    url: '/api/stripe/config',
    method: 'GET',
    category: 'billing',
    expectedStatus: 200
  },
  {
    id: 'stripe-billing-tiers',
    name: 'Stripe Billing Tiers',
    description: 'Test Stripe billing tiers endpoint',
    url: '/api/stripe/billing-tiers',
    method: 'GET',
    category: 'billing',
    expectedStatus: 200
  },
  {
    id: 'stripe-subscription-counts',
    name: 'Stripe Subscription Counts',
    description: 'Test Stripe subscription counts endpoint',
    url: '/api/stripe/subscription-counts',
    method: 'GET',
    category: 'billing',
    expectedStatus: 200
  },
  {
    id: 'stripe-migration-status',
    name: 'Stripe Migration Status',
    description: 'Test Stripe migration status endpoint',
    url: '/api/stripe/migration-status',
    method: 'GET',
    category: 'billing',
    expectedStatus: 200
  },
  {
    id: 'stripe-billing-overrides',
    name: 'Stripe Billing Overrides',
    description: 'Test Stripe billing overrides endpoint',
    url: '/api/stripe/billing-overrides',
    method: 'GET',
    category: 'billing',
    expectedStatus: 200
  },
  
  // Jobs endpoints (Global Admin required)
  {
    id: 'jobs-list',
    name: 'Jobs List',
    description: 'Test jobs list endpoint (Global Admin required)',
    url: '/api/jobs',
    method: 'GET',
    category: 'auth',
    expectedStatus: 200
  },
  {
    id: 'jobs-status-overview',
    name: 'Jobs Status Overview',
    description: 'Test jobs status overview (Global Admin required)',
    url: '/api/jobs/status/overview',
    method: 'GET',
    category: 'auth',
    expectedStatus: 200
  },
  {
    id: 'jobs-health',
    name: 'Jobs Health',
    description: 'Test jobs health endpoint (Global Admin required)',
    url: '/api/jobs/health',
    method: 'GET',
    category: 'auth',
    expectedStatus: 200
  },
  
  // Mailgun endpoints
  {
    id: 'mailgun-send',
    name: 'Mailgun Send',
    description: 'Test Mailgun email sending endpoint',
    url: '/api/mailgun/send',
    method: 'POST',
    category: 'billing',
    expectedStatus: 200
  }
]

const CATEGORY_ICONS = {
  discovery: '🔍',
  health: '💚',
  rooms: '🏠',
  company: '🏢',
  auth: '🔐',
  billing: '💳'
}

// Test categories with descriptions
const CATEGORY_DESCRIPTIONS = {
  health: 'System health, performance metrics, and server status endpoints',
  rooms: 'Room management, lobby statistics, and room control endpoints',
  discovery: 'Room discovery, company hydration, and search endpoints',
  company: 'Company management, usage statistics, and company-specific endpoints',
  auth: 'Admin authentication, user management, and system administration endpoints',
  billing: 'Billing, payments, Stripe integration, and financial endpoints'
}

const EMAIL_TYPES = [
  { value: 'account-created', label: 'Account Created', description: 'Welcome email for new account' },
  { value: 'password-reset', label: 'Password Reset', description: 'Password reset link email' },
  { value: 'company-invitation', label: 'Company Invitation', description: 'Company invitation email' },
  { value: 'company-removal', label: 'Company Removal', description: 'Company access revocation email' },
  { value: 'subscription-change', label: 'Subscription Change', description: 'Subscription plan change notification' },
  { value: 'subscription-charge-success', label: 'Subscription Charge Success', description: 'Successful payment notification' },
  { value: 'subscription-charge-failed', label: 'Subscription Charge Failed', description: 'Failed payment notification' }
]

export default function EndpointTestingPanel({ onBack }: EndpointTestingPanelProps) {
  const theme = useTheme()
  const [testResults, setTestResults] = useState<TestResult[]>([])
  const [isRunning, setIsRunning] = useState(false)
  const [selectedTests, setSelectedTests] = useState<string[]>([])
  const [customUrl, setCustomUrl] = useState('')
  const [customMethod, setCustomMethod] = useState<'GET' | 'POST' | 'PUT' | 'DELETE'>('GET')
  const [selectedResult, setSelectedResult] = useState<TestResult | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  
  // Email testing state
  const [testEmail, setTestEmail] = useState('')
  const [selectedEmailType, setSelectedEmailType] = useState<string>('account-created')
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [emailTestResult, setEmailTestResult] = useState<any>(null)

  const runTest = useCallback(async (test: EndpointTest): Promise<TestResult> => {
    const startTime = Date.now()
    const result: TestResult = {
      id: `${test.id}-${Date.now()}`,
      name: test.name,
      url: test.url,
      method: test.method,
      status: 'pending',
      timestamp: Date.now()
    }

    try {
      // Prefer relative URLs in dev to leverage Vite proxy and avoid CORS
      const isDev = !!((import.meta as any).env && (import.meta as any).env.DEV)
      const baseUrl = getServerBaseUrl()
      const fullUrl = isDev && test.url.startsWith('/') ? test.url : (test.url.startsWith('http') ? test.url : `${baseUrl}${test.url}`)
      
      // Build headers and attach Authorization if available
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...test.headers
      }
      try {
        const { authService } = await import('../../services/back4app')
        const currentUser = authService.getCurrentUser?.()
        const sessionToken = currentUser?.getSessionToken?.()
        if (sessionToken) {
          headers['Authorization'] = `Bearer ${sessionToken}`
        }
      } catch (e) {
        // ignore if auth service not available
      }

      const options: RequestInit = {
        method: test.method,
        headers,
        // Include credentials in prod; in dev, proxy keeps same-origin
        credentials: isDev ? 'same-origin' : 'include'
      }

      if (test.body && test.method !== 'GET') {
        options.body = JSON.stringify(test.body)
      }

      const response = await fetch(fullUrl, options)
      const responseTime = Date.now() - startTime
      
      let responseData
      try {
        responseData = await response.json()
      } catch {
        responseData = await response.text()
      }

      result.statusCode = response.status
      result.responseTime = responseTime
      result.response = responseData

      if (response.ok) {
        result.status = 'success'
      } else if (response.status >= 500) {
        result.status = 'error'
        result.error = `Server error: ${response.status} ${response.statusText}`
      } else {
        result.status = 'warning'
        result.error = `Client error: ${response.status} ${response.statusText}`
      }

    } catch (error) {
      result.status = 'error'
      result.error = error instanceof Error ? error.message : 'Unknown error'
      result.responseTime = Date.now() - startTime
    }

    return result
  }, [])

  const runSelectedTests = useCallback(async () => {
    if (selectedTests.length === 0) return

    setIsRunning(true)
    const results: TestResult[] = []

    for (const testId of selectedTests) {
      const test = ENDPOINT_TESTS.find(t => t.id === testId)
      if (test) {
        const result = await runTest(test)
        results.push(result)
        setTestResults(prev => [...prev, result])
      }
    }

    setIsRunning(false)
  }, [selectedTests, runTest])

  const runAllTests = useCallback(async () => {
    setIsRunning(true)
    setTestResults([])

    for (const test of ENDPOINT_TESTS) {
      const result = await runTest(test)
      setTestResults(prev => [...prev, result])
    }

    setIsRunning(false)
  }, [runTest])

  const runCustomTest = useCallback(async () => {
    if (!customUrl) return

    const test: EndpointTest = {
      id: 'custom',
      name: 'Custom Test',
      description: 'Custom endpoint test',
      url: customUrl,
      method: customMethod,
      category: 'health'
    }

    const result = await runTest(test)
    setTestResults(prev => [...prev, result])
  }, [customUrl, customMethod, runTest])

  const sendTestEmail = useCallback(async () => {
    if (!testEmail || !selectedEmailType) return

    setIsSendingEmail(true)
    setEmailTestResult(null)

    try {
      // Get current user session token for authentication
      const { authService } = await import('../../services/back4app')
      const currentUser = authService.getCurrentUser?.()
      const sessionToken = currentUser?.getSessionToken?.()

      if (!sessionToken) {
        setEmailTestResult({
          success: false,
          error: 'You must be logged in to test emails',
          message: 'Authentication required'
        })
        setIsSendingEmail(false)
        return
      }

      // Determine server base URL
      const baseUrl = getServerBaseUrl()
      const isDev = !!((import.meta as any).env && (import.meta as any).env.DEV)
      const fullUrl = isDev ? '/api/email/test' : `${baseUrl}/api/email/test`

      // Call the server endpoint
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        credentials: isDev ? 'same-origin' : 'include',
        body: JSON.stringify({
          recipientEmail: testEmail,
          emailType: selectedEmailType
        })
      })

      const result = await response.json()

      if (!response.ok) {
        setEmailTestResult({
          success: false,
          error: result.error || `Server returned ${response.status}`,
          message: result.message || 'Failed to send test email'
        })
      } else {
        setEmailTestResult(result)
      }
    } catch (error: any) {
      console.error('Error sending test email:', error)
      setEmailTestResult({
        success: false,
        error: error.message || 'Unknown error',
        message: 'Failed to send test email'
      })
    } finally {
      setIsSendingEmail(false)
    }
  }, [testEmail, selectedEmailType])

  const toggleTestSelection = (testId: string) => {
    setSelectedTests(prev => 
      prev.includes(testId) 
        ? prev.filter(id => id !== testId)
        : [...prev, testId]
    )
  }

  const clearResults = () => {
    setTestResults([])
  }

  const copyResult = (result: TestResult) => {
    const resultText = JSON.stringify({
      name: result.name,
      url: result.url,
      method: result.method,
      status: result.status,
      statusCode: result.statusCode,
      responseTime: result.responseTime,
      response: result.response,
      error: result.error,
      timestamp: new Date(result.timestamp).toISOString()
    }, null, 2)
    
    navigator.clipboard.writeText(resultText).then(() => {
      // You could add a toast notification here if you have a notification system
      console.log('Result copied to clipboard')
    }).catch(err => {
      console.error('Failed to copy result:', err)
    })
  }

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main' }} />
      case 'warning':
        return <WarningIcon sx={{ color: 'warning.main' }} />
      default:
        return <InfoIcon sx={{ color: 'text.secondary' }} />
    }
  }

  const statusChipSx = (status: TestResult['status']) => {
    const bg = statusAccent(theme, status)
    return {
      bgcolor: bg,
      color: theme.palette.getContrastText(bg),
    }
  }

  const groupedTests = ENDPOINT_TESTS.reduce((acc, test) => {
    if (!acc[test.category]) acc[test.category] = []
    acc[test.category].push(test)
    return acc
  }, {} as Record<string, EndpointTest[]>)

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 3 }}>
        <IconButton onClick={onBack} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon />
        </IconButton>
        <BugReportIcon sx={{ color: 'error.main', fontSize: 28 }} />
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Endpoint Testing
        </Typography>
      </Box>

      {/* Controls */}
      <Card sx={{ marginBottom: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={runAllTests}
              disabled={isRunning}
              sx={{ bgcolor: 'error.main', '&:hover': { bgcolor: 'error.dark' } }}
            >
              Run All Tests
            </Button>
            
            <Button
              variant="outlined"
              onClick={runSelectedTests}
              disabled={isRunning || selectedTests.length === 0}
              startIcon={<PlayIcon />}
            >
              Run Selected ({selectedTests.length})
            </Button>
            
            <Button
              variant="outlined"
              onClick={clearResults}
              startIcon={<RefreshIcon />}
            >
              Clear Results
            </Button>

            {isRunning && <CircularProgress size={24} />}
          </Box>
        </CardContent>
      </Card>

      {/* Test Selection */}
      <Card sx={{ marginBottom: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600 }}>
            Available Tests
          </Typography>
          
          {Object.entries(groupedTests).map(([category, tests]) => (
            <Accordion key={category} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <span style={{ fontSize: '1.2em' }}>{CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS]}</span>
                  <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                    {category} Tests ({tests.length})
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', ml: 2, flex: 1 }}>
                    {CATEGORY_DESCRIPTIONS[category as keyof typeof CATEGORY_DESCRIPTIONS]}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {tests.map((test) => (
                    <Grid item xs={12} sm={6} md={4} key={test.id}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          border: 1,
                          borderColor: selectedTests.includes(test.id) ? 'error.main' : 'divider',
                          borderWidth: selectedTests.includes(test.id) ? 2 : 1,
                          bgcolor: selectedTests.includes(test.id)
                            ? alpha(theme.palette.error.main, 0.08)
                            : 'background.paper',
                          backgroundImage: 'none',
                        }}
                        onClick={() => toggleTestSelection(test.id)}
                      >
                        <CardContent sx={{ padding: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
                            <Chip 
                              label={test.method} 
                              size="small" 
                              sx={{ 
                                ...categoryChipPalette(theme, test.category),
                                fontWeight: 600
                              }} 
                            />
                            <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                              {test.url}
                            </Typography>
                          </Box>
                          <Typography component="span" variant="subtitle2" sx={{ fontWeight: 600, marginBottom: 0.5, display: 'block' }}>
                            {test.name}
                          </Typography>
                          <Typography component="span" variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                            {test.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>

      {/* Email Service Testing */}
      <Card sx={{ marginBottom: 3, border: 2, borderColor: 'error.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
            <EmailIcon sx={{ color: 'error.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Email Service Testing
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: 3 }}>
            Test the centralized EmailService with Mailgun SMTP. Requires MAILGUNUSER and MAILGUNPASSWORD in the server environment.
            All emails use userDataHelper and companyDataHelper for consistent data extraction.
          </Typography>
          
          <Grid container spacing={2} sx={{ marginBottom: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Recipient Email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                fullWidth
                required
                helperText="Email address to receive the test email"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Email Type"
                value={selectedEmailType}
                onChange={(e) => setSelectedEmailType(e.target.value)}
                SelectProps={{ native: true }}
                fullWidth
                helperText="Select the type of email to test"
              >
                {EMAIL_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={sendTestEmail}
                disabled={!testEmail || isSendingEmail}
                startIcon={isSendingEmail ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
                fullWidth
                sx={{ 
                  height: '56px',
                  bgcolor: 'error.main', 
                  '&:hover': { bgcolor: 'error.dark' },
                  '&:disabled': { bgcolor: 'action.disabledBackground' }
                }}
              >
                {isSendingEmail ? 'Sending...' : 'Send Test Email'}
              </Button>
            </Grid>
          </Grid>

          {EMAIL_TYPES.find(t => t.value === selectedEmailType) && (
            <Alert severity="info" sx={{ marginBottom: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: 0.5 }}>
                {EMAIL_TYPES.find(t => t.value === selectedEmailType)?.label}
              </Typography>
              <Typography variant="caption">
                {EMAIL_TYPES.find(t => t.value === selectedEmailType)?.description}
              </Typography>
            </Alert>
          )}

          {emailTestResult && (
            <Alert 
              severity={emailTestResult.success ? 'success' : 'error'}
              sx={{ marginTop: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {emailTestResult.success ? (
                  <CheckCircleIcon sx={{ fontSize: 24, mt: 0.5 }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 24, mt: 0.5 }} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: 1 }}>
                    {emailTestResult.success ? '✓ Email Sent Successfully' : '✗ Email Send Failed'}
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    {emailTestResult.message || emailTestResult.error}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 1 }}>
                    {emailTestResult.emailType && (
                      <Chip 
                        label={`Type: ${EMAIL_TYPES.find(t => t.value === emailTestResult.emailType)?.label || emailTestResult.emailType}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {emailTestResult.recipientEmail && (
                      <Chip 
                        label={`To: ${emailTestResult.recipientEmail}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {emailTestResult.result?.messageId && (
                      <Chip 
                        label={`Message ID: ${emailTestResult.result.messageId.substring(0, 20)}...`}
                        size="small"
                        variant="outlined"
                        color="success"
                      />
                    )}
                  </Box>

                  {emailTestResult.details && (
                    <Box sx={{ marginTop: 2 }}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            View Error Details
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Paper sx={{ 
                            padding: 2, 
                            bgcolor: 'action.hover',
                            maxHeight: '300px',
                            overflow: 'auto'
                          }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                              {typeof emailTestResult.details === 'string' 
                                ? emailTestResult.details 
                                : JSON.stringify(emailTestResult.details, null, 2)}
                            </Typography>
                          </Paper>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}
                  
                  {(emailTestResult.result || (!emailTestResult.details && !emailTestResult.success)) && (
                    <Box sx={{ marginTop: 2 }}>
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            View Full Response
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Paper sx={{ 
                            padding: 2, 
                            bgcolor: 'action.hover',
                            maxHeight: '300px',
                            overflow: 'auto'
                          }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(emailTestResult, null, 2)}
                            </Typography>
                          </Paper>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}
                </Box>
              </Box>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Custom Test */}
      <Card sx={{ marginBottom: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: 600 }}>
            Custom Test
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="URL"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="/api/your-endpoint"
              sx={{ minWidth: 300 }}
            />
            <TextField
              select
              label="Method"
              value={customMethod}
              onChange={(e) => setCustomMethod(e.target.value as any)}
              SelectProps={{ native: true }}
              sx={{ minWidth: 120 }}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
            </TextField>
            <Button
              variant="outlined"
              onClick={runCustomTest}
              disabled={!customUrl}
              startIcon={<PlayIcon />}
            >
              Test
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Results */}
      {testResults.length > 0 && (
        <Card sx={{ maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0 }}>
            {/* Results Header */}
            <Box sx={{ 
              padding: 3, 
              borderBottom: 1,
              borderBottomColor: 'divider',
              bgcolor: 'action.hover',
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Test Results ({testResults.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip 
                  label={`${testResults.filter(r => r.status === 'success').length} Success`}
                  color="success"
                  size="small"
                />
                <Chip 
                  label={`${testResults.filter(r => r.status === 'error').length} Errors`}
                  color="error"
                  size="small"
                />
                <Chip 
                  label={`${testResults.filter(r => r.status === 'warning').length} Warnings`}
                  color="warning"
                  size="small"
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => {
                    const allResults = testResults.map(r => ({
                      name: r.name,
                      url: r.url,
                      method: r.method,
                      status: r.status,
                      statusCode: r.statusCode,
                      responseTime: r.responseTime,
                      error: r.error,
                      response: r.response
                    }))
                    navigator.clipboard.writeText(JSON.stringify(allResults, null, 2))
                  }}
                  sx={{ ml: 1 }}
                >
                  Copy All
                </Button>
              </Box>
            </Box>

            {/* Scrollable Results Container */}
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto',
              maxHeight: '60vh'
            }}>
              <List sx={{ padding: 0 }}>
                {testResults.map((result, index) => (
                  <React.Fragment key={result.id}>
                    <ListItem sx={{ 
                      padding: 2,
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}>
                      <ListItemIcon>
                        {getStatusIcon(result.status)}
                      </ListItemIcon>
                      <ListItemText
                        disableTypography
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 0.5, flexWrap: 'wrap' }}>
                            <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {result.name}
                            </Typography>
                            <Chip 
                              label={result.method}
                              size="small"
                              sx={categoryChipPalette(theme, 'health')}
                            />
                            {result.statusCode && (
                              <Chip 
                                label={result.statusCode}
                                size="small"
                                sx={statusChipSx(result.status)}
                              />
                            )}
                            {result.responseTime && (
                              <Chip 
                                label={`${result.responseTime}ms`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography component="span" variant="body2" sx={{ color: 'text.secondary', marginBottom: 1, display: 'block' }}>
                              {result.url}
                            </Typography>
                            {result.error && (
                              <Alert severity="error" sx={{ marginBottom: 1, marginTop: 1 }}>
                                {result.error}
                              </Alert>
                            )}
                            {result.response && (
                              <Accordion sx={{ marginTop: 1, boxShadow: 'none', border: 1, borderColor: 'divider' }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    View Response ({Object.keys(result.response).length} fields)
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: 0 }}>
                                  <Paper sx={{ 
                                    padding: 2, 
                                    bgcolor: 'action.hover',
                                    maxHeight: '200px',
                                    overflow: 'auto'
                                  }}>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                      {JSON.stringify(result.response, null, 2)}
                                    </Typography>
                                  </Paper>
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Tooltip title="Copy Result">
                          <IconButton onClick={() => copyResult(result)} size="small">
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Details">
                          <IconButton 
                            onClick={() => {
                              setSelectedResult(result)
                              setDetailsOpen(true)
                            }} 
                            size="small"
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                    {index < testResults.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Result Details Modal */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedResult && getStatusIcon(selectedResult.status)}
            <Typography variant="h6">
              {selectedResult?.name} - Test Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedResult && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Basic Information
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Name:</Typography>
                      <Typography variant="body2">{selectedResult.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>URL:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {selectedResult.url}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Method:</Typography>
                      <Typography variant="body2">{selectedResult.method}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Status:</Typography>
                      <Chip 
                        label={selectedResult.status} 
                        size="small"
                        sx={statusChipSx(selectedResult.status)}
                      />
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Performance & Timing
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Status Code:</Typography>
                      <Typography variant="body2">{selectedResult.statusCode || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Response Time:</Typography>
                      <Typography variant="body2">{selectedResult.responseTime ? `${selectedResult.responseTime}ms` : 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>Timestamp:</Typography>
                      <Typography variant="body2">{new Date(selectedResult.timestamp).toLocaleString()}</Typography>
                    </Box>
                  </Box>
                </Grid>

                {selectedResult.error && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Error Details
                    </Typography>
                    <Alert severity="error">
                      {selectedResult.error}
                    </Alert>
                  </Grid>
                )}

                {selectedResult.response && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Response Data
                    </Typography>
                    <Paper sx={{ 
                      padding: 2, 
                      bgcolor: 'action.hover',
                      maxHeight: '300px',
                      overflow: 'auto'
                    }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(selectedResult.response, null, 2)}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
          <Button 
            onClick={() => {
              if (selectedResult) {
                copyResult(selectedResult)
              }
            }}
            startIcon={<CopyIcon />}
            variant="outlined"
          >
            Copy Details
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
